import React from "react";
import { Images, X, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import api, { MEDIA } from "../lib/api";
import { useSettings } from "../contexts/SettingsContext";

export default function Portfolio() {
    const [items, setItems] = React.useState([]);
    const [lightbox, setLightbox] = React.useState(null);
    const { settings } = useSettings();

    React.useEffect(() => { api.get("/portfolio").then(r => setItems(r.data || [])); }, []);

    // OG / Twitter meta tags
    React.useEffect(() => {
        const siteName = settings?.site_name || settings?.company_name || "ccashop";
        const title = `Portofoliu — Proiecte realizate | ${siteName}`;
        const desc = "Instalări CCTV, interfoane, automatizări porți și sisteme smart home — fiecare proiect livrat cu garanție și certificat de instalare.";
        const url = window.location.href;
        const logo = settings?.logo_url || "";

        document.title = title;
        const setMeta = (prop, val, attr = "property") => {
            let el = document.querySelector(`meta[${attr}="${prop}"]`);
            if (!el) { el = document.createElement("meta"); el.setAttribute(attr, prop); document.head.appendChild(el); }
            el.setAttribute("content", val);
        };
        setMeta("og:type", "website");
        setMeta("og:title", title);
        setMeta("og:description", desc);
        setMeta("og:url", url);
        setMeta("og:site_name", siteName);
        if (logo) setMeta("og:image", logo.startsWith("http") ? logo : `${window.location.origin}${logo}`);
        setMeta("twitter:card", "summary_large_image", "name");
        setMeta("twitter:title", title, "name");
        setMeta("twitter:description", desc, "name");

        return () => { document.title = siteName; };
    }, [settings]);

    const imagesOf = (p) => {
        if (p.images?.length) return p.images;
        return [p.image_after, p.image_before, ...(p.gallery || [])].filter(Boolean);
    };
    const thumbOf = (p) => p.thumbnail || p.image_after || imagesOf(p)[0] || "";

    const open = (images, index = 0) => setLightbox({ images, index });
    const close = () => setLightbox(null);
    const next = (e) => { e.stopPropagation(); setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length })); };
    const prev = (e) => { e.stopPropagation(); setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length })); };

    React.useEffect(() => {
        if (!lightbox) return;
        const handler = (e) => {
            if (e.key === "ArrowRight") setLightbox(l => ({ ...l, index: (l.index + 1) % l.images.length }));
            if (e.key === "ArrowLeft") setLightbox(l => ({ ...l, index: (l.index - 1 + l.images.length) % l.images.length }));
            if (e.key === "Escape") close();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [lightbox]);

    return (
        <div data-testid="portfolio-page">
            {/* Hero */}
            <section className="bg-foreground text-background rounded-b-[2rem] overflow-hidden">
                <div className="container-x py-14 md:py-20">
                    <div className="overline text-primary mb-3">Lucrările noastre</div>
                    <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl font-semibold tracking-tight max-w-2xl">
                        Proiecte realizate
                    </h1>
                    <p className="text-background/70 mt-4 max-w-xl text-base sm:text-lg">
                        Instalări CCTV, interfoane, automatizări și sisteme smart home — fiecare proiect livrat cu garanție și certificat de instalare.
                    </p>
                </div>
            </section>

            <div className="container-x py-12 sm:py-16">
                {items.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground">
                        <div className="text-4xl mb-4">📷</div>
                        <p>Proiectele vor fi adăugate în curând.</p>
                    </div>
                ) : (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
                        {items.map((p, i) => {
                            const thumb = thumbOf(p);
                            const imgs = imagesOf(p);
                            return (
                                <div key={p.portfolio_id}
                                    className="break-inside-avoid bg-card rounded-2xl overflow-hidden border border-border/60 hover:border-primary/30 hover:shadow-lg transition-all duration-300 animate-fade-up"
                                    style={{ animationDelay: `${Math.min(i, 5) * 0.07}s` }}
                                    data-testid={`portfolio-${p.portfolio_id}`}>
                                    <button
                                        onClick={() => imgs.length && open(imgs, 0)}
                                        className="block w-full bg-muted relative group cursor-zoom-in"
                                    >
                                        {thumb && (
                                            <img
                                                src={MEDIA(thumb)}
                                                alt={p.title}
                                                className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                                            />
                                        )}
                                        {/* Overlay */}
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm text-foreground rounded-full p-3 shadow-lg">
                                                <Images className="h-5 w-5" />
                                            </div>
                                        </div>
                                        {imgs.length > 1 && (
                                            <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                                                <Images className="h-3 w-3" />
                                                {imgs.length}
                                            </div>
                                        )}
                                    </button>
                                    <div className="p-4">
                                        {(p.category || p.location) && (
                                            <div className="flex items-center gap-3 mb-1.5">
                                                {p.category && <span className="overline">{p.category}</span>}
                                                {p.location && (
                                                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-mono">
                                                        <MapPin className="h-3 w-3" />{p.location}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <h3 className="font-heading text-lg font-semibold mb-1">{p.title}</h3>
                                        {p.description && <p className="text-sm text-muted-foreground line-clamp-2">{p.description}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 z-[200] bg-black/92 backdrop-blur-sm flex items-center justify-center"
                    onClick={close}
                    data-testid="lightbox">
                    {/* Close */}
                    <button
                        onClick={close}
                        className="absolute top-4 right-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2.5 transition-all z-10">
                        <X className="h-5 w-5" />
                    </button>

                    {/* Nav arrows */}
                    {lightbox.images.length > 1 && (
                        <>
                            <button onClick={prev} className="absolute left-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all z-10">
                                <ChevronLeft className="h-6 w-6" />
                            </button>
                            <button onClick={next} className="absolute right-4 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all z-10">
                                <ChevronRight className="h-6 w-6" />
                            </button>
                        </>
                    )}

                    {/* Image */}
                    <img
                        src={MEDIA(lightbox.images[lightbox.index])}
                        alt=""
                        className="max-h-[88vh] max-w-[88vw] object-contain rounded-xl shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Counter + thumbnails */}
                    <div className="absolute bottom-5 left-0 right-0 flex flex-col items-center gap-3">
                        {lightbox.images.length > 1 && (
                            <div className="flex gap-1.5">
                                {lightbox.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={(e) => { e.stopPropagation(); setLightbox(l => ({ ...l, index: i })); }}
                                        className={`w-1.5 h-1.5 rounded-full transition-all ${i === lightbox.index ? "bg-white w-4" : "bg-white/40"}`}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="text-white/50 text-xs font-mono">
                            {lightbox.index + 1} / {lightbox.images.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
